<script>
    import httpService from '@/services/core/http.service';
    import AdminGrid from '@/components/shared/AdminGrid';
    import { DxColumn } from 'devextreme-vue/data-grid';

    export default {
        components: {
            AdminGrid,
            DxColumn
        },
        data() {
            return {
                dataSource: httpService.getDxDataSource('diagnostics/grid')
            };
        },
        methods: {
            // fileAvailable and blobPath have no column of their own. That is safe here, and it is
            // checked rather than assumed: devextreme's grid data source adapter builds its remote
            // load options from data/filter/group/pageIndex/pageSize/requireTotalCount/skip/sort/take
            // and never sets `select`, and devextreme-aspnet-data only forwards a `select` it is
            // given -- so the server's projection comes back whole. Companies relies on the same
            // thing: its onCellPrepared reads e.data.status, which has no column either.
            //
            // Even so, neither behaviour below depends on that holding. Availability falls back to
            // the expiry date, which IS a visible column, and the copy button hides itself rather
            // than silently copying nothing.
            isFileAvailable(row) {
                if (typeof row?.fileAvailable === 'boolean') return row.fileAvailable;
                if (!row?.blobExpiresOn) return true;
                return new Date(row.blobExpiresOn) > new Date();
            },
            // Grey out rows whose zip has aged out. They are still worth showing -- who uploaded and
            // when is often the answer on its own -- but they are records, not downloads, and a row
            // that looks live when the file is gone sends someone chasing a 404.
            onCellPrepared(e) {
                if (e.rowType === 'data' && !this.isFileAvailable(e.data)) {
                    e.cellElement.style.color = '#ccc';
                }
            },
            formatSize(rowData) {
                const bytes = rowData.sizeBytes || 0;
                if (bytes < 1024) return `${bytes} B`;
                if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
                return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
            },
            copyPath(item) {
                // The snapshot path is the handle everything else takes -- the Claude diagnostics
                // endpoints and the Slack thread both key on it -- so copying it is the action people
                // actually need from this page.
                if (item.data?.blobPath) navigator.clipboard?.writeText(item.data.blobPath);
            }
        }
    };
</script>

<template>
    <div class="main-content-header">
        <h3>
            <i class="bi bi-phone"></i>
            Mobile diagnostics
        </h3>
    </div>
    <div class="main-content-body">
        <AdminGrid
            :data-source="dataSource"
            storage-key="diagnostics"
            ref="diagnosticsGrid"
            :show-selection="false"
            @cell-prepared="onCellPrepared"
        >
            <!--
                The id is here to be SAID and typed. Referring to a snapshot by its blob path
                (user-12991/26-08-31-18_31_21.zip) is unusable out loud and easy to mistype -- the
                first real support round trip was spent confirming one. A short integer is the handle
                people can trade in a Slack thread; the clipboard button still hands over the real
                path for anything that needs it.
            -->
            <DxColumn data-field="id" caption="ID" data-type="number" :width="90" />
            <DxColumn
                data-field="uploadedOn"
                caption="Uploaded (UTC)"
                data-type="date"
                format="MM/dd/yyyy hh:mm a"
                sort-order="desc"
                :width="180"
            />
            <DxColumn data-field="userName" caption="User" />
            <DxColumn data-field="userEmail" caption="Email" :visible="false" />
            <DxColumn data-field="companyName" caption="Company" />
            <DxColumn data-field="appVersion" caption="App" :width="100" />
            <DxColumn data-field="osVersion" caption="OS" :width="130" />
            <DxColumn data-field="sizeBytes" caption="Size" data-type="number" :width="100" :calculate-display-value="formatSize" />
            <DxColumn data-field="blobExpiresOn" caption="File until" data-type="date" format="MM/dd/yyyy" :width="130" />

            <template #actions="item">
                <div class="d-flex justify-content-end gap-2">
                    <i class="bi bi-clipboard cursor-pointer" title="Copy snapshot path" @click="copyPath(item)"></i>
                    <a
                        v-if="isFileAvailable(item.data)"
                        :href="item.data.blobUrl"
                        target="_blank"
                        rel="noopener"
                        title="Open the file (needs Azure storage access)"
                    >
                        <i class="bi bi-download"></i>
                    </a>
                </div>
            </template>
        </AdminGrid>
    </div>
</template>
